import { ArrowLeftMedium, ArrowRightMedium } from '@blueprint/icons';
import { ButtonIconOnly } from '@blueprint/ui';
import { FC, SyntheticEvent, useState } from 'react';
import { defaultForSaleAreas } from './defaultForSaleAreas';
import { defaultToRentAreas } from './defaultToRentAreas';
import {
    ContentContainer,
    DesktopLink,
    MobileLink,
    SearchSuggestionsTownsAndCitiesWrapper,
    StyledArrowsWrapperMobile,
    StyledArrowsWrapperTablet,
    StyledHeading,
    TabButton,
    TabButtons,
    TabContent,
    TownsAndCitiesHeadlineWrapper
} from './SearchSuggestionsTownsAndCities.styles';
import {
    trackBrowseAllCitiesTownsUK,
    trackIndividualLocationForSale,
    trackIndividualLocationToRent,
    trackSeeAllUKForSale,
    trackSeeAllUKToRent
} from './SearchSuggestionsTownsAndCities.tracking';
import { SearchSuggestionsTownsAndCitiesProps } from './SearchSuggestionsTownsAndCities.types';
import { SearchSuggestionTownsAndCityLinks } from './SearchSuggestionTownsAndCitiesLinks';

const MAIN_SITE_LINK = 'https://www.zoopla.co.uk';
const sitemapLink = `${MAIN_SITE_LINK}/sitemap/`;

enum Tab {
    ForSale = 'for-sale-tab',
    ToRent = 'to-rent-tab'
}

export const SearchSuggestionsTownsAndCities: FC<
    SearchSuggestionsTownsAndCitiesProps
> = ({
    forSaleAreas = defaultForSaleAreas,
    toRentAreas = defaultToRentAreas
}) => {
    const [selectedTabId, setSelectedTabId] = useState(Tab.ForSale);
    const [carouselIndex, setCarouselIndex] = useState(0);

    const isShowingForSale = selectedTabId === Tab.ForSale;
    const isShowingToRent = selectedTabId === Tab.ToRent;

    const decrementCarouselIndex = () => {
        setCarouselIndex((previousIndex) => previousIndex - 1);
    };

    const incrementCarouselIndex = () => {
        setCarouselIndex((previousIndex) => previousIndex + 1);
    };

    return (
        <SearchSuggestionsTownsAndCitiesWrapper>
            <TownsAndCitiesHeadlineWrapper>
                <StyledHeading size="5">
                    Discover towns and cities
                </StyledHeading>
            </TownsAndCitiesHeadlineWrapper>
            <ContentContainer>
                <TabButtons role="tablist">
                    <TabButton
                        href="/for-sale/"
                        role="tab"
                        aria-selected={isShowingForSale}
                        aria-controls="suggestions-content-for-sale"
                        id="suggestions-tab-for-sale"
                        isActive={isShowingForSale}
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            setSelectedTabId(Tab.ForSale);
                        }}
                        data-testid="for-sale-tab"
                    >
                        For sale
                    </TabButton>
                    <TabButton
                        role="tab"
                        aria-selected={isShowingToRent}
                        aria-controls="suggestions-content-to-rent"
                        id="suggestions-tab-to-rent"
                        isActive={isShowingToRent}
                        data-testid="to-rent-tab"
                        href="/to-rent/"
                        onClick={(event: SyntheticEvent) => {
                            event.preventDefault();
                            setSelectedTabId(Tab.ToRent);
                        }}
                    >
                        To rent
                    </TabButton>
                </TabButtons>

                <DesktopLink
                    href={sitemapLink}
                    variant="tertiary"
                    data-testid="explore_more-cta-desktop"
                    onClick={() => trackBrowseAllCitiesTownsUK}
                >
                    Explore more towns &amp; cities
                </DesktopLink>

                <StyledArrowsWrapperMobile>
                    <ButtonIconOnly
                        disabled={carouselIndex === 0}
                        aria-label="Arrow left"
                        variant="tertiary"
                        iconOnly
                        data-testid={`arrow_full_left_mobile`}
                        onClick={decrementCarouselIndex}
                    >
                        <ArrowLeftMedium />
                    </ButtonIconOnly>
                    <ButtonIconOnly
                        disabled={carouselIndex === 3}
                        aria-label="Arrow right"
                        variant="tertiary"
                        iconOnly
                        data-testid={`arrow_full_right_mobile`}
                        onClick={incrementCarouselIndex}
                    >
                        <ArrowRightMedium />
                    </ButtonIconOnly>
                </StyledArrowsWrapperMobile>

                <StyledArrowsWrapperTablet>
                    <ButtonIconOnly
                        disabled={carouselIndex === 0}
                        aria-label="Arrow left"
                        variant="tertiary"
                        iconOnly
                        data-testid={`arrow_full_left_tablet`}
                        onClick={decrementCarouselIndex}
                    >
                        <ArrowLeftMedium />
                    </ButtonIconOnly>
                    <ButtonIconOnly
                        disabled={carouselIndex === 1}
                        aria-label="Arrow right"
                        variant="tertiary"
                        iconOnly
                        data-testid={`arrow_full_right_tablet`}
                        onClick={incrementCarouselIndex}
                    >
                        <ArrowRightMedium />
                    </ButtonIconOnly>
                </StyledArrowsWrapperTablet>

                <TabContent
                    role="tabpanel"
                    aria-labelledby="suggestions-tab-for-sale"
                    id="suggestions-content-for-sale"
                    isVisible={isShowingForSale}
                    carouselIndex={carouselIndex}
                >
                    <SearchSuggestionTownsAndCityLinks
                        areas={forSaleAreas}
                        trackSeeAll={trackSeeAllUKForSale}
                        trackSeeIndividual={trackIndividualLocationForSale}
                    />
                </TabContent>

                <TabContent
                    role="tabpanel"
                    aria-labelledby="suggestions-tab-to-rent"
                    id="suggestions-content-to-rent"
                    isVisible={isShowingToRent}
                    carouselIndex={carouselIndex}
                >
                    <SearchSuggestionTownsAndCityLinks
                        areas={toRentAreas}
                        trackSeeAll={trackSeeAllUKToRent}
                        trackSeeIndividual={trackIndividualLocationToRent}
                    />
                </TabContent>
            </ContentContainer>
            <MobileLink
                href={sitemapLink}
                variant="tertiary"
                data-testid="explore_more-cta-mobile"
                onClick={() => trackBrowseAllCitiesTownsUK()}
            >
                Explore more towns &amp; cities
            </MobileLink>
        </SearchSuggestionsTownsAndCitiesWrapper>
    );
};
